import { SVButton, SVForm, SVCurrencyInput } from '@/components/core';
import { MESSAGES } from '@/constants/messages';
import { useChangeCustomerDepositRules } from '@/hooks/requests/useCustomers';
import {
  ChangeDepositRulesFormFields,
  CustomerDetails,
} from '@/types/customers';
import { convertToNumber } from '@/utils/currency';
import showToast from '@/utils/toast';
import {
  Dialog,
  DialogContent,
  Typography,
  DialogActions,
} from '@mui/material';
import { useForm } from 'react-hook-form';

type CustomerChangeDepositRulesModalProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  customerDetails: CustomerDetails;
};

export default function CustomerChangeDepositRulesModal({
  open,
  setOpen,
  customerDetails,
}: CustomerChangeDepositRulesModalProps) {
  const { changeCustomerDepositRules, isChangeCustomerDepositRulesPending } =
    useChangeCustomerDepositRules();
  const formMethods = useForm<ChangeDepositRulesFormFields>({
    defaultValues: {
      min: customerDetails.minimumInitDeposit
        ? `${customerDetails.minimumInitDeposit.value}`
        : '0',
      max: customerDetails.maximumInitDeposit
        ? `${customerDetails.maximumInitDeposit.value}`
        : '0',
      uuid: customerDetails.uuid,
    },
    mode: 'onBlur',
  });

  async function handleSubmit(data: ChangeDepositRulesFormFields) {
    const minNumber = convertToNumber(data.min);
    const maxNumber = convertToNumber(data.max);

    try {
      await changeCustomerDepositRules({
        uuid: data.uuid,
        min: `${minNumber}`,
        max: `${maxNumber}`,
      });
      showToast({
        type: 'success',
        data: {
          content: 'Customer deposit rules have been succesfully updated',
        },
      });
      setOpen(false);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <Dialog fullWidth open={open} onClose={() => setOpen(false)}>
      <DialogContent>
        <Typography variant="h6" component="h2" mb={2}>
          Change Customer Deposit Rules
        </Typography>
        <SVForm<ChangeDepositRulesFormFields>
          methods={formMethods}
          onSubmit={handleSubmit}>
          <SVCurrencyInput
            name="min"
            label="Initial Deposit Minimum"
            required={false}
            rules={{ min: 0 }}
          />
          <SVCurrencyInput
            name="max"
            label="Initial Deposit Maximum"
            required={false}
            rules={{ min: 0 }}
          />
        </SVForm>
      </DialogContent>
      <DialogActions>
        <SVButton onClick={() => setOpen(false)} variant="tertiary">
          Cancel
        </SVButton>
        <SVButton
          isLoading={isChangeCustomerDepositRulesPending}
          onClick={formMethods.handleSubmit(handleSubmit)}>
          Change
        </SVButton>
      </DialogActions>
    </Dialog>
  );
}
